import React, { useState, useEffect, memo } from 'react';
import { useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, CalendarDays, Bell, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const SettingsCalendar = memo(function SettingsCalendar() {
  const { language } = useLanguage();
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [mealReminders, setMealReminders] = useState({
    breakfast: true, lunch: true, dinner: true,
    breakfast_time: '08:00', lunch_time: '12:00', dinner_time: '18:00',
  });
  const [schedulingReminders, setSchedulingReminders] = useState(false);
  const [reminderDays, setReminderDays] = useState(7);
  const [lastScheduled, setLastScheduled] = useState(null);

  useEffect(() => {
    api.get('/calendar/status').then(res => setCalendarConnected(res.data.connected)).catch(() => {});
    api.get('/calendar/meal-reminder-status').then(res => {
      if (res.data.reminders && Object.keys(res.data.reminders).length > 0) {
        const r = res.data.reminders;
        setMealReminders(prev => ({
          breakfast: r.breakfast ?? prev.breakfast, lunch: r.lunch ?? prev.lunch, dinner: r.dinner ?? prev.dinner,
          breakfast_time: r.breakfast_time || prev.breakfast_time, lunch_time: r.lunch_time || prev.lunch_time, dinner_time: r.dinner_time || prev.dinner_time,
        }));
        setLastScheduled(r.last_scheduled_at || null);
      }
    }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar') === 'success') {
      setCalendarConnected(true);
      toast.success(language === 'fr' ? 'Google Calendar connecte!' : 'Google Calendar connected!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('calendar') === 'error') {
      toast.error(language === 'fr' ? 'Erreur de connexion Google Calendar' : 'Google Calendar connection error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [language]);

  const handleConnect = async () => {
    setCalendarLoading(true);
    try {
      const res = await api.get('/oauth/calendar/login');
      window.location.href = res.data.authorization_url;
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
      setCalendarLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/calendar/disconnect');
      setCalendarConnected(false);
      toast.success(language === 'fr' ? 'Google Calendar deconnecte' : 'Google Calendar disconnected');
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleScheduleMealReminders = async () => {
    setSchedulingReminders(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
      const res = await api.post('/calendar/schedule-meal-reminders', {
        days: reminderDays, timezone: tz, meals: mealReminders,
      });
      if (res.data.success) {
        setLastScheduled(new Date().toISOString());
        toast.success(language === 'fr' ? `${res.data.created} rappels de repas ajoutes a ton agenda!` : `${res.data.created} meal reminders added to your calendar!`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.includes('non connect')) {
        toast.error(language === 'fr' ? 'Connecte d\'abord ton Google Calendar' : 'Connect Google Calendar first');
      } else {
        toast.error(language === 'fr' ? 'Erreur lors de la planification' : 'Scheduling error');
      }
    } finally {
      setSchedulingReminders(false);
    }
  };

  const mealTypes = [
    { key: 'breakfast', timeKey: 'breakfast_time', fr: 'Dejeuner', en: 'Breakfast', icon: '🌅' },
    { key: 'lunch', timeKey: 'lunch_time', fr: 'Diner', en: 'Lunch', icon: '☀️' },
    { key: 'dinner', timeKey: 'dinner_time', fr: 'Souper', en: 'Dinner', icon: '🌙' },
  ];

  return (
    <div className="mt-8">
      <Card className="bg-card border-border" data-testid="calendar-sync-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-blue-400" />
            {language === 'fr' ? 'Synchro Google Calendar' : 'Google Calendar Sync'}
          </CardTitle>
          <CardDescription>
            {language === 'fr'
              ? 'Synchronise tes entraînements et rappels de repas directement dans ton agenda Google.'
              : 'Sync your workouts and meal reminders directly to your Google Calendar.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${calendarConnected ? 'bg-green-500/20' : 'bg-secondary'}`}>
                <CalendarDays className={`w-5 h-5 ${calendarConnected ? 'text-green-400' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="font-medium text-foreground">Google Calendar</p>
                <p className="text-sm text-muted-foreground">
                  {calendarConnected ? (language === 'fr' ? 'Connecte' : 'Connected') : (language === 'fr' ? 'Non connecte' : 'Not connected')}
                </p>
              </div>
            </div>
            {calendarConnected ? (
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-400 border-red-400/50 hover:bg-red-400/10 w-full sm:w-auto" data-testid="disconnect-calendar-btn">
                {language === 'fr' ? 'Deconnecter' : 'Disconnect'}
              </Button>
            ) : (
              <Button onClick={handleConnect} disabled={calendarLoading} className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto" data-testid="connect-calendar-btn">
                {calendarLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
                {language === 'fr' ? 'Brancher mon Agenda' : 'Sync Calendar'}
              </Button>
            )}
          </div>

          {calendarConnected && (
            <>
              <p className="text-xs text-muted-foreground text-center">
                {language === 'fr' ? 'Tes entraînements sont automatiquement ajoutes quand tu les logues.' : 'Your workouts are automatically added when you log them.'}
              </p>
              <div className="border-t border-border pt-4 space-y-4" data-testid="meal-reminders-section">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-green-400" />
                  <h4 className="font-semibold text-foreground text-sm">{language === 'fr' ? 'Rappels de repas' : 'Meal Reminders'}</h4>
                </div>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Programme des rappels dans ton agenda pour ne jamais oublier de logger tes repas.' : 'Schedule reminders in your calendar to never forget logging your meals.'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {mealTypes.map((meal) => (
                    <div key={meal.key} className="p-3 bg-secondary/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{meal.icon} {language === 'fr' ? meal.fr : meal.en}</span>
                        <Switch checked={mealReminders[meal.key]} onCheckedChange={(checked) => setMealReminders(prev => ({ ...prev, [meal.key]: checked }))} data-testid={`reminder-toggle-${meal.key}`} />
                      </div>
                      {mealReminders[meal.key] && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <Input type="time" value={mealReminders[meal.timeKey]} onChange={e => setMealReminders(prev => ({ ...prev, [meal.timeKey]: e.target.value }))} className="h-8 text-xs bg-background" data-testid={`reminder-time-${meal.key}`} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">{language === 'fr' ? 'Planifier pour' : 'Schedule for'}</Label>
                  <select value={reminderDays} onChange={e => setReminderDays(parseInt(e.target.value))} className="h-8 px-2 text-sm bg-secondary text-foreground rounded border border-border" data-testid="reminder-days-select">
                    <option value={3}>3 {language === 'fr' ? 'jours' : 'days'}</option>
                    <option value={7}>7 {language === 'fr' ? 'jours' : 'days'}</option>
                    <option value={14}>14 {language === 'fr' ? 'jours' : 'days'}</option>
                  </select>
                </div>
                <Button onClick={handleScheduleMealReminders} disabled={schedulingReminders} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="schedule-reminders-btn">
                  {schedulingReminders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {language === 'fr' ? 'Programmer les rappels' : 'Schedule Reminders'}
                </Button>
                {lastScheduled && (
                  <p className="text-xs text-muted-foreground text-center">
                    {language === 'fr' ? 'Dernière planification:' : 'Last scheduled:'} {new Date(lastScheduled).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                  </p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default SettingsCalendar;
